import React from 'react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useAwsContext } from './AwsContext';
import { RdsTable, Ec2Table, AsgTable } from './tables';
import { Box, Typography, Chip } from '@mui/material';
import { CRDAWSConfig } from '@internal/backstage-plugin-compute-resource-discovery-new-common';

// Helper function to group AWS configs by account ID and region
const groupAwsConfigs = (configs: CRDAWSConfig[]) => {
  const grouped: { [key: string]: CRDAWSConfig[] } = {};

  configs.forEach(config => {
    const key = `${config.accountId || 'unknown'}-${
      config.region || 'unknown'
    }`;
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(config);
  });

  return grouped;
};

export const Aws: React.FC<{ entity: Entity }> = ({ entity }) => {
  const { awsConfigs } = useAwsContext();
  const groupedConfigs = groupAwsConfigs(awsConfigs);

  return (
    <>
      {Object.entries(groupedConfigs).map(([groupKey, configs]) => {
        const firstConfig = configs[0];
        const accountId = firstConfig.accountId || 'Unknown Account';
        const region = firstConfig.region || 'Unknown Region';

        return (
          <Box key={groupKey} sx={{ mb: 4 }}>
            {/* Account/Region Header */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#FFF3E0',
                borderRadius: 1,
                border: '1px solid #FF9900',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ color: '#FF9900', fontWeight: 'bold' }}
                >
                  Account ID: {accountId}
                </Typography>
                <Chip
                  label={region}
                  size="small"
                  sx={{ backgroundColor: '#FF9900', color: 'white' }}
                />
              </Box>
            </Box>

            {/* Resource Tables for this account/region */}
            {configs.map((config, configIdx) => (
              <Box key={configIdx} sx={{ mb: 2 }}>
                {config.resourceTypes?.includes('rds') && (
                  <Box sx={{ mb: 2 }}>
                    <RdsTable
                      title="RDS Instances"
                      entityRef={stringifyEntityRef(entity)}
                      awsConfig={config}
                    />
                  </Box>
                )}
                {config.resourceTypes?.includes('ec2') && (
                  <Box sx={{ mb: 2 }}>
                    <Ec2Table
                      title="EC2 Instances"
                      entityRef={stringifyEntityRef(entity)}
                      awsConfig={config}
                    />
                  </Box>
                )}
                {config.resourceTypes?.includes('asg') && (
                  <Box sx={{ mb: 2 }}>
                    <AsgTable
                      title="Auto Scaling Groups"
                      entityRef={stringifyEntityRef(entity)}
                      awsConfig={config}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        );
      })}
    </>
  );
};
